<template>
  <div class="promo-rules-manager">
    <base-button
      type="primary"
      size="sm"
      @click="showRuleTypeSelector = true"
      v-if="!showRuleTypeSelector"
      class="mb-3"
    >
      <i class="tim-icons icon-simple-add mr-1"></i>
      Add rule
    </base-button>

    <!-- Rule Type Selector (shown when Add Rule clicked) -->
    <div v-if="showRuleTypeSelector" class="rule-type-selector mb-3">
      <p class="mb-2 small"><strong>Select rule type:</strong></p>
      <div class="d-flex flex-wrap gap-2 mb-2">
        <div
          v-for="ruleType in availableRuleTypes.filter(r => r.is_active)"
          :key="ruleType.id"
          class="rule-type-card-compact"
          @click="selectRuleType(ruleType)"
          :class="{ 'disabled': !ruleType.is_active }"
        >
          <div class="rule-type-name">{{ ruleType.name }}</div>
          <div class="rule-type-desc">{{ ruleType.description }}</div>
        </div>
      </div>
      <base-button
        link
        type="primary"
        size="sm"
        @click="showRuleTypeSelector = false"
      >
        Cancel
      </base-button>
    </div>

    <!-- Active Rules List -->
    <div v-if="rules.length > 0" class="rules-grid mt-2">
      <div
        v-for="(rule, index) in rules"
        :key="rule.id || index"
        class="rule-card"
      >
        <div class="rule-card-header">
          <badge type="info">{{ getRuleTypeName(rule.rule_type_id) }}</badge>
          <div class="rule-actions">
            <base-button
              size="sm"
              icon link
              @click="handleEditRule(rule, index)"
              class="btn-icon btn-sm text-white"
            >
              <i class="tim-icons icon-pencil"></i>
            </base-button>
            <base-button
              size="sm"
              icon link
              @click="handleRemoveRule(index)"
              class="btn-icon btn-sm text-white"
            >
              <i class="tim-icons icon-trash-simple"></i>
            </base-button>
          </div>
        </div>
        <div class="rule-card-body">
          <small class="text-white opacity-7">{{ formatRuleSummary(rule) }}</small>
        </div>
      </div>
    </div>

    <!-- Rule Configuration Modal -->
    <modal
      :show.sync="modals.configureRule"
      body-classes="p-0"
      modal-classes="modal-dialog-centered modal-lg"
    >
      <card
        type="secondary"
        header-classes="bg-white pb-5"
        body-classes="px-lg-5 py-lg-5"
        class="border-0 mb-0"
      >
        <template>
          <div class="mb-3">
            <h4 class="card-title">{{ ruleModalTitle }} rule</h4>
            <small class="text-white opacity-7 mt-0">{{ ruleModalDescription }}</small>
          </div>
          <!-- Minimum Quantity Rule Configuration -->
          <minimum-quantity-rule
            v-if="selectedRuleType?.rule_key === 'MIN_QUANTITY'"
            :rule-config="currentRuleConfig"
            :value-type="valueType"
            @update="updateRuleConfig"
          />

          <!-- Buy X Get Y Rule Configuration -->
          <buy-x-get-y-rule
            v-if="selectedRuleType?.rule_key === 'BUY_X_GET_Y'"
            :rule-config="currentRuleConfig"
            :available-games="availableGames"
            :available-items="availableItems"
            :available-gift-cards="availableGiftCards"
            @update="updateRuleConfig"
          />

          <!-- Minimum Value Rule Configuration -->
          <minimum-value-rule
            v-if="selectedRuleType?.rule_key === 'MIN_VALUE'"
            :rule-config="currentRuleConfig"
            @update="updateRuleConfig"
          />

          <!-- Bundle Price Rule Configuration -->
          <bundle-price-rule
            v-if="selectedRuleType?.rule_key === 'BUNDLE_PRICE'"
            :rule-config="currentRuleConfig"
            @update="updateRuleConfig"
          />

          <!-- Future rule types would go here -->

          <div class="mt-4 text-center">
            <base-button type="secondary" @click="cancelRuleConfig">
              Cancel
            </base-button>
            <base-button class="ml-2" type="primary" @click="saveRuleConfig">
              {{ editingRuleIndex !== null ? 'Update rule' : 'Add rule' }}
            </base-button>
          </div>
        </template>
      </card>
    </modal>
  </div>
</template>

<script>
import { Modal, Card, Badge } from 'src/components';
import MinimumQuantityRule from './MinimumQuantityRule.vue';
import BuyXGetYRule from './BuyXGetYRule.vue';
import MinimumValueRule from './MinimumValueRule.vue';
import BundlePriceRule from './BundlePriceRule.vue';

export default {
  name: 'PromoRulesManager',
  components: {
    Modal,
    Card,
    Badge,
    MinimumQuantityRule,
    BuyXGetYRule,
    MinimumValueRule,
    BundlePriceRule
  },
  props: {
    rules: {
      type: Array,
      default: () => []
    },
    availableRuleTypes: {
      type: Array,
      default: () => []
    },
    availableGames: {
      type: Array,
      default: () => []
    },
    availableItems: {
      type: Array,
      default: () => []
    },
    availableGiftCards: {
      type: Array,
      default: () => []
    },
    valueType: {
      type: Number,
      default: 0
    },
    currencySymbol: {
      type: String,
      default: '$'
    }
  },
  data() {
    return {
      modals: {
        configureRule: false
      },
      selectedRuleType: null,
      currentRuleConfig: {},
      currentRuleMode: null,
      editingRuleIndex: null,
      showRuleTypeSelector: false
    };
  },
  computed: {
    // For the value rule save guard. The editor reports its selected mode
    // explicitly; deriving from the values alone would misread a Range whose
    // bound is momentarily cleared.
    valueMode() {
      if (this.currentRuleMode) return this.currentRuleMode;
      const c = this.currentRuleConfig || {};
      const hasMin = c.min_amount != null;
      const hasMax = c.max_amount != null;
      if (hasMin && hasMax) return 'range';
      if (hasMax) return 'maximum';
      return 'minimum';
    },
    ruleModalTitle() {
      // The modal template appends the word "rule", so strip a trailing
      // "rule" from the stored name to avoid composing "Value rule rule".
      if (!this.selectedRuleType) return '';
      return this.selectedRuleType.name.replace(/\s+rule$/i, '');
    },
    ruleModalDescription() {
      return this.selectedRuleType ? this.selectedRuleType.description : '';
    }
  },
  methods: {
    selectRuleType(ruleType) {
      // A promo cannot carry both a bundle and a Buy X Get Y rule: their
      // discount semantics conflict and the pricing engines do not support
      // mixing them on one promo.
      const conflicts = { BUNDLE_PRICE: 'BUY_X_GET_Y', BUY_X_GET_Y: 'BUNDLE_PRICE' };
      const conflictKey = conflicts[ruleType.rule_key];
      if (conflictKey && this.rules.some(r => this.ruleKeyById(r.rule_type_id) === conflictKey)) {
        this.$notify({
          type: 'danger',
          message: 'A promo cannot combine a bundle price rule with a Buy X Get Y rule. Remove the other rule first.',
          icon: 'tim-icons icon-alert-circle-exc'
        });
        return;
      }
      this.showRuleTypeSelector = false;
      this.selectedRuleType = ruleType;
      this.currentRuleConfig = this.getDefaultConfig(ruleType.rule_key);
      this.editingRuleIndex = null;
      this.modals.configureRule = true;
    },
    ruleKeyById(ruleTypeId) {
      const rt = this.availableRuleTypes.find(t => t.id === ruleTypeId);
      return rt ? rt.rule_key : null;
    },
    handleEditRule(rule, index) {
      const ruleType = this.availableRuleTypes.find(rt => rt.id === rule.rule_type_id);
      this.selectedRuleType = ruleType;
      this.currentRuleConfig = JSON.parse(JSON.stringify(rule.rule_config));
      this.editingRuleIndex = index;
      this.modals.configureRule = true;
    },
    handleRemoveRule(index) {
      this.$emit('remove-rule', index);
    },
    updateRuleConfig(config, mode) {
      this.currentRuleConfig = config;
      this.currentRuleMode = mode || null;
    },
    saveRuleConfig() {
      if (this.selectedRuleType && this.selectedRuleType.rule_key === 'MIN_VALUE') {
        const c = this.currentRuleConfig || {};
        const mode = this.valueMode;
        let error = '';
        if (mode === 'minimum' && !(c.min_amount > 0)) {
          error = 'Enter a minimum value greater than zero';
        } else if (mode === 'maximum' && !(c.max_amount > 0)) {
          error = 'Enter a maximum value greater than zero';
        } else if (mode === 'range') {
          if (!(c.min_amount > 0) || !(c.max_amount > 0)) {
            error = 'Enter both range values greater than zero';
          } else if (c.max_amount < c.min_amount) {
            error = 'The maximum value must be at least the minimum value';
          }
        }
        if (error) {
          this.$notify({ type: 'danger', message: error, icon: 'tim-icons icon-alert-circle-exc' });
          return;
        }
      }
      if (this.selectedRuleType && this.selectedRuleType.rule_key === 'BUNDLE_PRICE') {
        const c = this.currentRuleConfig || {};
        let error = '';
        if (!(c.bundle_price > 0)) {
          error = 'Enter a bundle price greater than zero';
        } else if (!(c.min_quantity >= 1) || !(c.affected_quantity >= 1)) {
          error = 'Quantities must be at least one';
        } else if (c.affected_quantity > c.min_quantity) {
          error = 'How many get the price cannot exceed the minimum to qualify';
        }
        if (error) {
          this.$notify({ type: 'danger', message: error, icon: 'tim-icons icon-alert-circle-exc' });
          return;
        }
      }
      const rule = {
        rule_type_id: this.selectedRuleType.id,
        rule_config: this.currentRuleConfig,
        is_active: true
      };

      if (this.editingRuleIndex !== null) {
        this.$emit('update-rule', this.editingRuleIndex, rule);
      } else {
        this.$emit('add-rule', rule);
      }

      this.modals.configureRule = false;
      this.resetRuleConfig();
    },
    cancelRuleConfig() {
      this.modals.configureRule = false;
      this.resetRuleConfig();
    },
    resetRuleConfig() {
      this.selectedRuleType = null;
      this.currentRuleConfig = {};
      this.currentRuleMode = null;
      this.editingRuleIndex = null;
    },
    getDefaultConfig(ruleKey) {
      switch (ruleKey) {
        case 'MIN_QUANTITY':
          return {
            min_quantity: 1,
            max_quantity: null,
            applies_to: 'total_participants',
            error_message: ''
          };
        case 'BUY_X_GET_Y':
          return {
            required_items: {
              item_type: 'games',
              item_ids: [],
              min_quantity: 1,
              match_mode: 'any',
              unit: 'booking'
            },
            discounted_items: {
              item_type: 'items',
              item_ids: [],
              discount_mode: 'additive',
              discount_type: 'free',
              discount_value: 100,
              max_quantity: 1,
              auto_apply_behavior: 'block_promo',
              unit: 'booking'
            },
            error_message: ''
          };
        case 'MIN_VALUE':
          return {
            min_amount: 0,
            max_amount: null,
            applies_to: 'cart_total',
            error_message: ''
          };
        case 'BUNDLE_PRICE':
          return {
            min_quantity: 1,
            affected_quantity: 1,
            bundle_price: null,
            price_mode: 'cart_total',
            allow_reapplication: false,
            error_message: ''
          };
        default:
          return {};
      }
    },
    getRuleTypeName(ruleTypeId) {
      const ruleType = this.availableRuleTypes.find(rt => rt.id === ruleTypeId);
      return ruleType ? ruleType.name : 'Unknown';
    },
    formatRuleSummary(rule) {
      const ruleType = this.availableRuleTypes.find(rt => rt.id === rule.rule_type_id);

      if (!ruleType) return 'Unknown rule';

      switch (ruleType.rule_key) {
        case 'MIN_QUANTITY':
          return this.formatMinQuantitySummary(rule.rule_config);
        case 'BUY_X_GET_Y':
          return this.formatBuyXGetYSummary(rule.rule_config);
        case 'MIN_VALUE':
          return this.formatMinValueSummary(rule.rule_config);
        case 'BUNDLE_PRICE':
          return this.formatBundlePriceSummary(rule.rule_config);
        default:
          return 'Custom rule';
      }
    },
    formatBundlePriceSummary(config) {
      const sym = this.currencySymbol;
      const price = `${sym}${Number(config.bundle_price || 0).toFixed(2)}`;
      const m = config.affected_quantity || 1;
      const modeText = {
        cart_total: `${m} for ${price} total`,
        line_item: `${m} ${m === 1 ? 'line' : 'lines'} at ${price} each`,
        quantity: `${m} ${m === 1 ? 'item' : 'items'} at ${price} each`
      }[config.price_mode] || `${m} for ${price}`;
      const repeat = config.allow_reapplication ? ', repeats per group' : '';
      return `Bundle: ${modeText}${repeat}`;
    },
    formatMinValueSummary(config) {
      const scope = config.applies_to === 'gift_cards' ? 'gift card value' : 'cart total';
      const sym = this.currencySymbol;
      const fmt = (n) => `${sym}${Number(n).toFixed(2)}`;
      const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

      const hasMin = config.min_amount != null;
      const hasMax = config.max_amount != null;

      if (hasMin && hasMax) {
        return cap(`${scope} between ${fmt(config.min_amount)} and ${fmt(config.max_amount)} required`);
      }
      if (hasMax) {
        return cap(`${scope} of ${fmt(config.max_amount)} or less required`);
      }
      return `Minimum ${scope} of ${fmt(config.min_amount)} required`;
    },
    formatMinQuantitySummary(config) {
      const labels = {
        'total_participants': ['participant', 'participants'],
        'line_items': ['line item', 'line items'],
        'unique_items': ['unique item', 'unique items']
      };
      const [singular, plural] = labels[config.applies_to] || [config.applies_to, config.applies_to];

      const hasMin = config.min_quantity != null;
      const hasMax = config.max_quantity != null;

      if (hasMin && hasMax) {
        const noun = config.max_quantity === 1 ? singular : plural;
        return `Between ${config.min_quantity} and ${config.max_quantity} ${noun} required`;
      }
      if (hasMax) {
        const noun = config.max_quantity === 1 ? singular : plural;
        return `Maximum ${config.max_quantity} ${noun} allowed`;
      }
      const noun = config.min_quantity === 1 ? singular : plural;
      return `Minimum ${config.min_quantity} ${noun} required`;
    },
    formatBuyXGetYSummary(config) {
      const req = config.required_items;
      const disc = config.discounted_items;
      const reqIsTicket = req.unit === 'ticket' && (req.item_type === 'games' || req.item_type === 'any');
      const discIsTicket = disc.unit === 'ticket' && disc.item_type === 'games';

      // Helper for singular/plural
      const pluralize = (word, count) => {
        if (word === 'games') return count === 1 ? 'event' : 'events';
        if (word === 'items') return count === 1 ? 'item' : 'items';
        if (word === 'gift_cards') return count === 1 ? 'gift card' : 'gift cards';
        return word;
      };

      // Format required items text
      let reqText = '';
      const reqQty = req.min_quantity || 1;
      if (reqIsTicket) {
        const ticketWord = reqQty === 1 ? 'ticket' : 'tickets';
        if (req.item_ids && req.item_ids.length > 0) {
          reqText = `${reqQty} ${ticketWord} to ${req.item_ids.length} specific ${pluralize('games', req.item_ids.length)}`;
        } else if (req.item_type === 'any') {
          reqText = `${reqQty} ${ticketWord} or items`;
        } else {
          reqText = `${reqQty} ${ticketWord}`;
        }
      } else if (req.item_type === 'any') {
        reqText = reqQty === 1 ? 'any item' : `${reqQty}+ items`;
      } else if (req.item_ids && req.item_ids.length > 0) {
        const itemWord = pluralize(req.item_type, req.item_ids.length);
        reqText = reqQty === 1
          ? `${req.item_ids.length} specific ${itemWord}`
          : `${reqQty}+ of ${req.item_ids.length} specific ${itemWord}`;
      } else {
        const itemWord = pluralize(req.item_type, reqQty);
        reqText = reqQty === 1 ? `any ${itemWord}` : `${reqQty}+ ${itemWord}`;
      }

      // Format discounted items text
      const maxQty = disc.max_quantity || 1;
      const discCount = disc.item_ids ? disc.item_ids.length : 0;

      let discText = '';
      let optionsText = '';

      if (discCount > 0) {
        const itemWord = discIsTicket
          ? (maxQty === 1 ? 'ticket' : 'tickets')
          : pluralize(disc.item_type, maxQty);
        discText = `${maxQty} ${itemWord}`;

        if (discCount > maxQty) {
          optionsText = discIsTicket
            ? ` (across ${discCount} events)`
            : ` (from ${discCount} options)`;
        }
      } else {
        const itemWord = discIsTicket
          ? (maxQty === 1 ? 'ticket' : 'tickets')
          : pluralize(disc.item_type, maxQty);
        discText = `${maxQty} ${itemWord}`;
      }

      // Format discount text
      let discountText = '';
      if (disc.discount_type === 'free') {
        discountText = 'free';
      } else if (disc.discount_type === 'percentage') {
        discountText = `${disc.discount_value}% off`;
      } else {
        discountText = `$${disc.discount_value} off`;
      }

      return `Buy ${reqText}, get ${discText} ${discountText}${optionsText}`;
    }
  }
};
</script>

<style scoped>
.rule-type-card-compact {
  padding: 10px 12px;
  border: 1px solid #344675;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(29, 140, 248, 0.05);
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}

.rule-type-card-compact:hover {
  border-color: #1d8cf8;
  background: rgba(29, 140, 248, 0.1);
  transform: translateY(-1px);
}

.rule-type-card-compact.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rule-type-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 3px;
}

.rule-type-desc {
  font-size: 0.75rem;
  color: #adb5bd;
  line-height: 1.3;
}

.gap-2 {
  gap: 0.5rem;
}

.rules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.rule-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid #344675;
  border-radius: 6px;
  padding: 12px;
}

.rule-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.rule-actions {
  display: flex;
  gap: 4px;
}

.rule-actions .btn-icon {
  padding: 4px 8px;
  min-width: auto;
}

.rule-actions .tim-icons {
  font-size: 12px;
}

.rule-card-body {
  margin-top: 4px;
}

.rule-description {
  margin: 0;
  font-size: 0.875rem;
  color: #adb5bd;
  line-height: 1.4;
}

.card-title {
  margin-bottom: 5px;
}


.card-description {
  font-size: 0.875rem;
  margin-bottom: 0;
}
</style>

<style>
.white-content .rule-type-card-compact {
  background: rgba(29, 140, 248, 0.04);
  border-color: rgba(0, 0, 0, 0.15);
}

.white-content .rule-type-card-compact:hover {
  background: rgba(29, 140, 248, 0.08);
  border-color: #1d8cf8;
}

.white-content .rule-type-name {
  color: rgba(0, 0, 0, 0.85);
}

.white-content .rule-type-desc {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .rule-card {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.12);
}

.white-content .rule-card .text-white {
  color: rgba(0, 0, 0, 0.7) !important;
}

.white-content .rule-description {
  color: rgba(0, 0, 0, 0.6);
}
</style>
